import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import urlParse from 'url-parse';
import qs from 'querystringify';
import {
  Card,
  Page,
} from '../../components';
import CompareBenefits from './benefits/CompareBenefits';
import CompareBenefitsRedesign from './benefits/CompareBenefitsRedesign';
import {
  getIFrameId,
  getTheme,
  getUserDetails,
  getIsRedesign,
} from '../../modules/selectors';
import api from '../../utils/services';
import sendHeightPostMessage from '../../utils/sendHeightPostMessage';
import { WindowEventListener } from '../../listeners';
import { Languages } from '../../utils/constants';

const propTypes = {
  iFrameId: PropTypes.string,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      planId: PropTypes.string,
      carrierId: PropTypes.string,
    }).isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    agentNpn: PropTypes.string,
    token: PropTypes.string,
    userId: PropTypes.string,
    userRole: PropTypes.string,
  }),
  // Listen for a theme change since it may change the components height
  theme: PropTypes.shape({}),
  isRedesignFromState: PropTypes.bool,
};
const defaultProps = {
  iFrameId: null,
  theme: null,
  user: null,
  isRedesignFromState: false,
};

// Note: This also allows the plan to be edited if the user is logged in
class Compare extends Component {
  constructor(props) {
    super(props);
    this.state = {
      comparedPlans: {},
      editBenefitId: null,
      editBenefitName: null,
      editCategoryName: null,
      editSubCategoryName: null,
      loading: true,
    };
  }

  componentDidMount() {
    const { location: { search } } = this.props;
    const { query } = urlParse(search);
    // eslint-disable-next-line prefer-const
    let { ids, languageId } = qs.parse(query);
    if (!languageId) {
      languageId = Languages.en_US;
    }
    const planIds = ids.split(',');
    const arr = [];
    for (let i = 0; i < planIds.length;) {
      arr.push(api.getPlanByIdAndLanguage(planIds[i], qs.stringify({ languageId }, true)));
      i += 1;
    }

    Promise.all(arr).then((response) => {
      this.setState({ comparedPlans: response, loading: false });
    });
    this.sendHeightUpdate();
  }

  componentDidUpdate() {
    this.sendHeightUpdate();
  }

  sendHeightUpdate = () => {
    const { iFrameId } = this.props;
    sendHeightPostMessage(iFrameId);
  }

  handleStartEdit = (event, benefitName, categoryName) => {
    // When using this function make sure to pass the name and value,
    // where name should be defined in the state.
    const {
      editBenefitId,
      editCategoryName,
      editBenefitName,
      editSubCategoryName,
    } = this.state;

    const { currentTarget: { attributes: { name: nameObject, value: valueObject } } } = event;
    let name; let value;
    // This is implemented until we get the knowledge of
    // null checking in nested destructuring.
    if (!!nameObject && !!valueObject) {
      name = nameObject.value;
      ({ value } = valueObject);
    }
    // Clicking again on the text editor, makes the name and value undefined.
    if (!name) {
      return;
    }

    // This initialization is to ensure to only open a single text box at a time.
    const initializationObject = {
      editBenefitId: null,
      editCategoryName: null,
      editBenefitName: null,
      editSubCategoryName: null,
    };
    delete initializationObject[name];
    this.setState({ ...initializationObject });

    // Prevent text reset on clicking the same field
    if (value === editBenefitId
       || value === editCategoryName
       || value === editBenefitName
       || value === editSubCategoryName) {
      return;
    }
    // BenefitId is a number, So if we get a number, convert it to an int.
    // eslint-disable-next-line no-restricted-globals
    if (!isNaN(value)) {
      this.setState({ [name]: parseInt(value, 10) });
    } else if (name === 'editSubCategoryName' || name === 'editBenefitName') { // Since editing a subcategory is dependent on a benefit name.
      this.setState({ [name]: { value, benefitName, categoryName } });
    } else {
      this.setState({ [name]: value });
    }
  }

  handleSaveEdit = (value, planId, benefitName) => {
    const {
      editBenefitId,
      editBenefitName,
      editCategoryName,
      editSubCategoryName,
      comparedPlans,
    } = this.state;
    const modifiedPlans = [...comparedPlans];
    // Edit by benefit Id
    if (editBenefitId) {
      api.updatePlanBenefit(editBenefitId, { value })
        .then(() => {
          this.setState(() => {
            modifiedPlans.forEach((plan) => {
              if (plan.id === planId) {
                const updateBenefit = plan.planBenefits.find(b => b.id === editBenefitId);
                updateBenefit.value = value;
              }
            });
            return {
              comparedPlans: modifiedPlans,
              editBenefitId: null,
            };
          });
        });
      // Edit by BenefitName
    } else if (editBenefitName) {
      // Send only the modified plan benefit's to the api.
      const modifiedPlanBenefit = [];
      modifiedPlans.forEach((plan, planIndex) => {
        plan.planBenefits.forEach((benefit, benefitIndex) => {
          if (benefit.name === editBenefitName.value
            && benefit.category === editBenefitName.categoryName) {
            modifiedPlans[planIndex].planBenefits[benefitIndex].name = value;
            modifiedPlanBenefit.push(modifiedPlans[planIndex].planBenefits[benefitIndex]);
          }
        });
      });
      this.handleBatchPlanBenefitUpdate(modifiedPlanBenefit, modifiedPlans);
      // Edit by Category name
    } else if (editCategoryName) {
      const modifiedPlanBenefit = [];
      modifiedPlans.forEach((plan, planIndex) => {
        plan.planBenefits.forEach((benefit, benefitIndex) => {
          if (benefit.category === editCategoryName) {
            modifiedPlans[planIndex].planBenefits[benefitIndex].category = value;
            modifiedPlanBenefit.push(modifiedPlans[planIndex].planBenefits[benefitIndex]);
          }
        });
      });
      this.handleBatchPlanBenefitUpdate(modifiedPlanBenefit, modifiedPlans);
      // Edit by Sub category name
    } else if (editSubCategoryName) {
      const modifiedPlanBenefit = [];
      modifiedPlans.forEach((plan, planIndex) => {
        plan.planBenefits.forEach((benefit, benefitIndex) => {
          if (benefit.name === benefitName
              && benefit.subcategory === editSubCategoryName.value
              && benefit.category === editSubCategoryName.categoryName) {
            if (benefit.subcategory === editSubCategoryName.value) {
              modifiedPlans[planIndex].planBenefits[benefitIndex].subcategory = value;
              modifiedPlanBenefit.push(modifiedPlans[planIndex].planBenefits[benefitIndex]);
            }
          }
        });
      });
      this.handleBatchPlanBenefitUpdate(modifiedPlanBenefit, modifiedPlans);
    }
  }

  handleBatchPlanBenefitUpdate = (modifiedPlanBenefits, modifiedPlans) => {
    api.updateBatchPlanBenefits(modifiedPlanBenefits).then(() => {
      this.setState({
        comparedPlans: modifiedPlans,
        editSubCategoryName: null,
        editCategoryName: null,
        editBenefitId: null,
        editBenefitName: null,
      });
    });
  }

  handleCancelEdit = () => {
    this.setState({
      editBenefitId: null,
      editCategoryName: null,
      editBenefitName: null,
      editSubCategoryName: null,
    });
  }

  render() {
    const {
      location: { search },
      match: { params: { carrierId } },
      user,
      isRedesignFromState,
    } = this.props;
    const {
      comparedPlans,
      editBenefitId,
      loading,
      editBenefitName,
      editCategoryName,
      editSubCategoryName,
    } = this.state;
    const { query } = urlParse(search);
    const { frameless, isRedesign } = qs.parse(query);
    const isRedesignQuery = isRedesign !== undefined && (isRedesign === 'true' || isRedesign === 'True');
    const plans = [];

    if (comparedPlans.length > 0) {
      for (let i = 0; i < comparedPlans.length;) {
        plans.push(comparedPlans[i]);
        i += 1;
      }

      const displayComparePage = ((!isRedesignFromState && !isRedesignQuery) ? (
        <CompareBenefits
          editAllowed={!!user}
          editBenefitId={editBenefitId}
          editBenefitName={editBenefitName}
          editCategoryName={editCategoryName}
          editSubCategoryName={editSubCategoryName}
          onStartEdit={this.handleStartEdit}
          onSaveEdit={this.handleSaveEdit}
          onCancelEdit={this.handleCancelEdit}
          plans={plans}
          frameless={frameless !== undefined}
        />
      ) : (
        <CompareBenefitsRedesign
          editAllowed={!!user}
          editBenefitId={editBenefitId}
          editBenefitName={editBenefitName}
          editCategoryName={editCategoryName}
          editSubCategoryName={editSubCategoryName}
          onStartEdit={this.handleStartEdit}
          onSaveEdit={this.handleSaveEdit}
          onCancelEdit={this.handleCancelEdit}
          plans={plans}
          frameless={frameless !== undefined}
        />
      ));

      if (frameless !== undefined) {
        return (
          <div>
            {displayComparePage}
            <WindowEventListener event="resize" onEvent={this.sendHeightUpdate} />
          </div>
        );
      }

      return (
        <Page
          breadcrumbs={user ? [{ content: 'Plans', href: `/carriers/${carrierId}/plans` }] : undefined}
          fullWidth
        >
          <Card loading={loading}>
            <Card.Block>
              {displayComparePage}
            </Card.Block>
          </Card>
        </Page>
      );
    }
    return null;
  }
}

function mapStateToProps(state) {
  return {
    iFrameId: getIFrameId(state),
    theme: getTheme(state),
    user: getUserDetails(state),
    isRedesignFromState: getIsRedesign(state),
  };
}
Compare.propTypes = propTypes;
Compare.defaultProps = defaultProps;
export default connect(mapStateToProps)(Compare);
